/**
 * The built-in U-shaped (half-turn) stair (defs/stairU.ts) as a data spec —
 * the most "formula-heavy" example of the library.
 *
 * HOW IT WORKS
 *  - Placement: a LINE object; the drawn run is the footprint WIDTH, the
 *    side holding both flight ends. The far (+n) edge is the LANDING strip
 *    (as deep as one flight is wide); two parallel flights hang from it.
 *  - Legs: the left and right flight have INDEPENDENT lengths
 *    (`leftLegLength`, `rightLegLength`). A leg can never be longer than the
 *    footprint allows (depth − landing), so the drawing clamps them
 *    (`leftRun`, `rightRun`).
 *  - Stairwell: any width beyond two flights is the open well between them.
 *    The outline steps around it — the inner edges of the flights run up to
 *    the landing, no edge crosses the well. With flights of half the width
 *    the two inner edges meet and become the center divider.
 *  - Treads: the step total (step over the combined run) is split over the
 *    legs by their share of that run (each keeps at least one step); every
 *    flight spaces its treads evenly. Two `index` repeats draw them.
 *  - Walk line: up the left flight, across the landing, down the right one,
 *    with a tick at the bottom and an arrow head at the top. `reverse
 *    direction` swaps which end gets the tick and which the arrow.
 *  - Why the depth is linked: the footprint depth always equals the landing
 *    plus the LONGER leg (`depthLink`). Changing a leg or the flight width
 *    moves the depth; resizing the depth directly (panel / gizmo) shifts
 *    BOTH legs by the same amount instead, so their difference is kept.
 *  - Handles: three depth handles on the open (−n) side, the landing edge
 *    stays put — one on each flight's end (drags just that leg) and one in
 *    the middle of the bottom edge (drags both legs together).
 *  - Steps: `step` ↔ `number of steps` over the combined run of both legs.
 *
 * Every shape is formula-driven, so every group uses the `fixed` (identity)
 * resize rule and reads the live `width` / `depth` through the formulas. The
 * authored points below are the drawing at the default size and params.
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec } from '../spec';

/** Shortest a flight can get (m) — also the leg params' min. */
const MIN_LEG = 0.2;

const FIXED = { u: 'fixed', n: 'fixed' } as const;

export const stairUSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'LINE',
  constraint: { kind: 'FREE' },
  defaultSize: { width: 2.0, depth: 2.4, height: 3 },
  params: [
    { key: 'leftLegLength', kind: 'float', label: 'left leg', default: 1.5, min: MIN_LEG, decimals: 2, distance: true, order: 11.1 },
    { key: 'rightLegLength', kind: 'float', label: 'right leg', default: 1.5, min: MIN_LEG, decimals: 2, distance: true, order: 11.2 },
    { key: 'flightWidth', kind: 'float', label: 'flight width', default: 0.9, min: 0.5, max: 2.0, decimals: 2, distance: true, order: 11.3 },
    { key: 'treadDepth', kind: 'float', label: 'step', default: 0.25, min: 0.15, max: 0.4, decimals: 2, distance: true, order: 11.4 },
    { key: 'stepCount', kind: 'int', label: 'number of steps', default: 12, min: 2, order: 11.5 },
    { key: 'reversed', kind: 'boolean', label: 'reverse direction', default: false },
  ],
  formulas: [
    // ── Footprint ──
    { key: 'halfWidth', label: 'Half width', expr: 'width / 2', note: 'Half the footprint width: the flights sit at ±halfWidth.' },
    { key: 'halfDepth', label: 'Half depth', expr: 'depth / 2', note: 'Half the footprint depth: landing edge at +halfDepth, open side at −halfDepth.' },
    { key: 'fw', label: 'Flight width', expr: 'max(0.1, min(flightWidth, width / 2))', note: 'Effective flight width: at most half the width (two flights side by side).' },

    // ── Legs ──
    { key: 'leftLeg', label: 'Left leg', expr: `max(${MIN_LEG}, leftLegLength)`, note: 'Left leg as the steps and the linked depth use it (never below the minimum).' },
    { key: 'rightLeg', label: 'Right leg', expr: `max(${MIN_LEG}, rightLegLength)`, note: 'Right leg as the steps and the linked depth use it.' },
    { key: 'longestLeg', label: 'Longest possible leg', expr: 'max(0.05, depth - fw)', note: 'Longest leg the current footprint can draw (depth minus the landing).' },
    { key: 'leftRun', label: 'Drawn left leg', expr: 'min(leftLeg, longestLeg)', note: 'Drawn length of the left flight (clamped to the footprint).' },
    { key: 'rightRun', label: 'Drawn right leg', expr: 'min(rightLeg, longestLeg)', note: 'Drawn length of the right flight (clamped to the footprint).' },
    { key: 'depthShift', label: 'Leg shift on depth resize', expr: `max(${MIN_LEG}, depth - fw) - max(leftLeg, rightLeg)`, note: 'How far both legs must move so the longer one fills a directly resized depth.' },

    // ── Key lines along the depth ──
    { key: 'landingN', label: 'Landing edge', expr: 'halfDepth - fw', note: 'Where the landing strip starts (its boundary to both flights).' },
    { key: 'leftEndN', label: 'Left flight end', expr: 'landingN - leftRun', note: 'Open end (bottom) of the left flight.' },
    { key: 'rightEndN', label: 'Right flight end', expr: 'landingN - rightRun', note: 'Open end (bottom) of the right flight.' },

    // ── Steps ──
    { key: 'totalSteps', label: 'Total steps', expr: 'max(2, round((leftRun + rightRun) / max(0.05, treadDepth)))', note: 'Steps drawn in total: the step over the combined drawn run.' },
    {
      key: 'leftSteps',
      label: 'Steps on the left',
      expr: 'min(totalSteps - 1, max(1, round(totalSteps * leftRun / (leftRun + rightRun))))',
      note: "Left flight's share of the steps, by its share of the run (at least 1 each).",
    },
    { key: 'rightSteps', label: 'Steps on the right', expr: 'totalSteps - leftSteps', note: 'The rest of the steps go to the right flight.' },

    // ── Walk line ──
    { key: 'leftWalkU', label: 'Left flight centerline', expr: '-halfWidth + fw / 2', note: 'Centerline of the left flight.' },
    { key: 'rightWalkU', label: 'Right flight centerline', expr: 'halfWidth - fw / 2', note: 'Centerline of the right flight.' },
    { key: 'walkLandingN', label: 'Landing centerline', expr: 'halfDepth - fw / 2', note: 'The walk line crosses the landing through its middle.' },
    { key: 'leftWalkEndN', label: 'Walk line end, left', expr: 'leftEndN + min(0.2, 0.25 * leftRun)', note: 'The walk line stops a little inside the left flight end.' },
    { key: 'rightWalkEndN', label: 'Walk line end, right', expr: 'rightEndN + min(0.2, 0.25 * rightRun)', note: 'The walk line stops a little inside the right flight end.' },
    { key: 'arrowSize', label: 'Arrow size', expr: 'min(0.15, fw * 0.3)', note: 'Size of the start tick and the arrow head.' },
    { key: 'startU', label: 'Walk line start (width)', expr: 'if(reversed, rightWalkU, leftWalkU)', note: 'Bottom of the stairs (tick): the left flight, or the right one when reversed.' },
    { key: 'startN', label: 'Walk line start (depth)', expr: 'if(reversed, rightWalkEndN, leftWalkEndN)', note: 'Depth position of the start tick.' },
    { key: 'headU', label: 'Arrow head (width)', expr: 'if(reversed, leftWalkU, rightWalkU)', note: 'Top of the stairs (arrow head): the other flight end.' },
    { key: 'headN', label: 'Arrow head (depth)', expr: 'if(reversed, leftWalkEndN, rightWalkEndN)', note: 'Depth position of the arrow head.' },
  ],
  shapes: [
    {
      id: 'outline',
      name: 'Outline',
      kind: 'polyline',
      closed: true,
      points: [
        [-1, 1.2],
        [1, 1.2],
        [1, -1.2],
        [0.1, -1.2],
        [0.1, 0.3],
        [-0.1, 0.3],
        [-0.1, -1.2],
        [-1, -1.2],
      ],
      expr: {
        'p0.u': '-halfWidth',
        'p0.n': 'halfDepth',
        'p1.u': 'halfWidth',
        'p1.n': 'halfDepth',
        'p2.u': 'halfWidth',
        'p2.n': 'rightEndN',
        'p3.u': 'halfWidth - fw',
        'p3.n': 'rightEndN',
        'p4.u': 'halfWidth - fw',
        'p4.n': 'landingN',
        'p5.u': '-halfWidth + fw',
        'p5.n': 'landingN',
        'p6.u': '-halfWidth + fw',
        'p6.n': 'leftEndN',
        'p7.u': '-halfWidth',
        'p7.n': 'leftEndN',
      },
    },
    {
      id: 'landingLeft',
      name: 'Left landing edge',
      kind: 'polyline',
      closed: false,
      points: [
        [-1, 0.3],
        [-0.1, 0.3],
      ],
      expr: { 'p0.u': '-halfWidth', 'p0.n': 'landingN', 'p1.u': '-halfWidth + fw', 'p1.n': 'landingN' },
    },
    {
      id: 'landingRight',
      name: 'Right landing edge',
      kind: 'polyline',
      closed: false,
      points: [
        [0.1, 0.3],
        [1, 0.3],
      ],
      expr: { 'p0.u': 'halfWidth - fw', 'p0.n': 'landingN', 'p1.u': 'halfWidth', 'p1.n': 'landingN' },
    },
    {
      id: 'leftTread',
      name: 'Left flight tread',
      kind: 'polyline',
      closed: false,
      points: [
        [-1, -0.95],
        [-0.1, -0.95],
      ],
      // Tread i + 1 of the flight (the open end and the landing edge are drawn already).
      expr: {
        'p0.u': '-halfWidth',
        'p0.n': 'leftEndN + (i + 1) * leftRun / leftSteps',
        'p1.u': '-halfWidth + fw',
        'p1.n': 'leftEndN + (i + 1) * leftRun / leftSteps',
      },
    },
    {
      id: 'rightTread',
      name: 'Right flight tread',
      kind: 'polyline',
      closed: false,
      points: [
        [0.1, 0.05],
        [1, 0.05],
      ],
      expr: {
        'p0.u': 'halfWidth - fw',
        'p0.n': 'landingN - (i + 1) * rightRun / rightSteps',
        'p1.u': 'halfWidth',
        'p1.n': 'landingN - (i + 1) * rightRun / rightSteps',
      },
    },
    {
      id: 'walkLine',
      name: 'Walk line',
      kind: 'polyline',
      closed: false,
      points: [
        [-0.55, -1.0],
        [-0.55, 0.75],
        [0.55, 0.75],
        [0.55, -1.0],
      ],
      expr: {
        'p0.u': 'leftWalkU',
        'p0.n': 'leftWalkEndN',
        'p1.u': 'leftWalkU',
        'p1.n': 'walkLandingN',
        'p2.u': 'rightWalkU',
        'p2.n': 'walkLandingN',
        'p3.u': 'rightWalkU',
        'p3.n': 'rightWalkEndN',
      },
    },
    {
      // Both flight ends of the walk line run along −n, so the tick is always across the u axis.
      id: 'walkStart',
      name: 'Walk line start tick',
      kind: 'polyline',
      closed: false,
      points: [
        [-0.7, -1.0],
        [-0.4, -1.0],
      ],
      expr: { 'p0.u': 'startU - arrowSize', 'p0.n': 'startN', 'p1.u': 'startU + arrowSize', 'p1.n': 'startN' },
    },
    {
      // The walk line arrives heading −n, so the chevron opens towards +n.
      id: 'walkHead',
      name: 'Walk line arrow head',
      kind: 'polyline',
      closed: false,
      points: [
        [0.4, -0.85],
        [0.55, -1.0],
        [0.7, -0.85],
      ],
      expr: {
        'p0.u': 'headU - arrowSize',
        'p0.n': 'headN + arrowSize',
        'p1.u': 'headU',
        'p1.n': 'headN',
        'p2.u': 'headU + arrowSize',
        'p2.n': 'headN + arrowSize',
      },
    },
  ],
  groups: [
    { id: 'body', name: 'Outline and landing', shapeIds: ['outline', 'landingLeft', 'landingRight'], resize: FIXED },
    { id: 'leftTreads', name: 'Left flight treads', shapeIds: ['leftTread'], resize: FIXED, repeat: { kind: 'index', count: { expr: 'leftSteps - 1' } } },
    { id: 'rightTreads', name: 'Right flight treads', shapeIds: ['rightTread'], resize: FIXED, repeat: { kind: 'index', count: { expr: 'rightSteps - 1' } } },
    { id: 'walk', name: 'Walk line', shapeIds: ['walkLine', 'walkStart', 'walkHead'], resize: FIXED },
  ],
  handles: {
    depth: [
      // End of the left flight: drags just the left leg (pulled is measured from the landing edge).
      { label: 'Left leg handle', side: -1, at: { u: 'leftWalkU', n: 'leftEndN' }, assign: [{ param: 'leftLegLength', expr: `max(${MIN_LEG}, pulled - fw)` }] },
      // End of the right flight: drags just the right leg.
      { label: 'Right leg handle', side: -1, at: { u: 'rightWalkU', n: 'rightEndN' }, assign: [{ param: 'rightLegLength', expr: `max(${MIN_LEG}, pulled - fw)` }] },
      // Middle of the bottom edge: both legs shift together so the longer one lands on the cursor.
      {
        label: 'Both legs handle',
        side: -1,
        u: 0,
        assign: [
          { param: 'leftLegLength', expr: `max(${MIN_LEG}, leftLeg + max(${MIN_LEG}, pulled - fw) - max(leftLeg, rightLeg))` },
          { param: 'rightLegLength', expr: `max(${MIN_LEG}, rightLeg + max(${MIN_LEG}, pulled - fw) - max(leftLeg, rightLeg))` },
        ],
      },
    ],
  },
  // Depth = landing + the longer leg; a direct depth resize shifts both legs instead.
  depthLink: {
    label: 'Linked depth',
    expr: 'fw + max(leftLeg, rightLeg)',
    onResize: [
      { param: 'leftLegLength', expr: `max(${MIN_LEG}, leftLeg + depthShift)` },
      { param: 'rightLegLength', expr: `max(${MIN_LEG}, rightLeg + depthShift)` },
    ],
  },
  stepSyncs: [{ label: 'Step size and step count', step: 'treadDepth', count: 'stepCount', run: 'leftLeg + rightLeg', minCount: 2, exactStep: false }],
  model3d: null,
};
